import { atom, useAtomValue } from 'jotai';
import {
    invoiceLineItemDispensesTableFiltersAtom,
    invoiceLineItemDispensesTableStatsAtom,
    invoiceLineItemDispensesTableViewAtom,
    invoiceLineItemDispensesModelAtom,
} from './invoice_line_item_dispenses_atoms';
import { useCallback, useEffect, useMemo } from 'react';
import { tableDataFilterItems } from '../../../core/table/utils/tableDataFilterItems';
import { tableDataPageItems } from '../../../core/table/utils/tableDataPageItems';
import { tableDataSortItems } from '../../../core/table/utils/tableDataSortItems';
import { useImmerAtom } from 'jotai-immer';
import { TableDataState } from '../../../core/table/table.types';
import { InvoiceLineItemDispensesDataItem } from './invoice_line_item_dispenses_list_types';
import { ngDelegate } from '../../../core/services/ng.delegate';
import cpOrderInvoicesRepo from '../data/cpOrderInvoicesRepo';

const LineItemDispensesData = atom<TableDataState<InvoiceLineItemDispensesDataItem>>({ items: [], dataItems: [] });

type UseInvoiceLineItemDispensesDataReturn = {
    items: InvoiceLineItemDispensesDataItem[];
    getListAsync: (id: string) => void;
};

export const useInvoiceLineItemDispensesData = (): UseInvoiceLineItemDispensesDataReturn => {
    const [view] = useImmerAtom(invoiceLineItemDispensesTableViewAtom);
    const [filters] = useImmerAtom(invoiceLineItemDispensesTableFiltersAtom);
    const [stats, setStats] = useImmerAtom(invoiceLineItemDispensesTableStatsAtom);
    const [data, setData] = useImmerAtom(LineItemDispensesData);

    const filtered = useMemo(() => {
        return tableDataFilterItems<InvoiceLineItemDispensesDataItem>(data.dataItems, view.cols, filters);
    }, [data.dataItems, view.cols, filters]);

    const sorted = tableDataSortItems<InvoiceLineItemDispensesDataItem>(filtered, view.cols, view.sort);
    const paged = tableDataPageItems<InvoiceLineItemDispensesDataItem>(sorted, stats.paging.size, stats.paging.page);

    const getListAsync = useCallback(
        async (id: string) => {
            try {
                ngDelegate.wait(true, true);
                const res = await cpOrderInvoicesRepo.getLineItemDispenses(id);

                setData(() => ({ items: res, dataItems: res }));
            } catch (err: any) {
                setData(() => ({ items: [], dataItems: [] }));
            } finally {
                ngDelegate.wait(false);
            }
        },
        [setData],
    );

    useEffect(() => {
        setStats(draft => {
            draft.fltStats.filtered = filtered.length;
            draft.fltStats.total = data.dataItems.length;
            draft.paging.total = Math.ceil(filtered.length / draft.paging.size);
        });
    }, [filtered.length, data.dataItems.length, setStats, stats.paging.size]);

    return {
        items: paged,
        getListAsync,
    };
};

export const useDispenses = (): InvoiceLineItemDispensesDataItem[] => {
    // Returns the current value of the invoice line item dispenses model atom
    const invoice = useAtomValue(invoiceLineItemDispensesModelAtom);
    return invoice;
};
